import { supabase } from '@/integrations/supabase/client';
import { getFromClienteCache, setClienteCache } from './ordensServicoCache';
import { Funcionario } from '@/types';
import { convertHoursToDecimal } from './timeConversion';

export const calcularValorFaturamentoOS = async (numeroOS: string): Promise<number> => {
  try {
    const { data: os, error: osError } = await supabase
      .from('ordens_servico')
      .select('horas_faturadas, cliente_cadastro_id, data_real, empresa_nome, tipo_chamado')
      .eq('numero', numeroOS)
      .maybeSingle();

    if (osError || !os) return 0;

    if (os.tipo_chamado === 'atividadeprojeto' || os.tipo_chamado === 'orcamento') return 0;

    const horasFaturadas = convertHoursToDecimal(os.horas_faturadas || '0');
    if (horasFaturadas === 0) return 0;

    const valorHoraCliente = await getValorHoraClienteVigente(
      os.cliente_cadastro_id,
      os.empresa_nome,
      os.data_real
    );

    return Math.round(horasFaturadas * valorHoraCliente * 100) / 100;
  } catch {
    return 0;
  }
};

export const getValorHoraClienteVigente = async (
  clienteCadastroId: string | null,
  clienteNome: string | null,
  dataOS: string,
  options?: { usarValorMaisRecente?: boolean }
): Promise<number> => {
  const cacheKey = options?.usarValorMaisRecente
    ? `cliente-${clienteCadastroId || clienteNome}-latest`
    : `cliente-${clienteCadastroId || clienteNome}-${dataOS}`;
  const cachedValue = getFromClienteCache(cacheKey);

  if (cachedValue !== null) return cachedValue;

  try {
    let clienteId: string | null = null;

    if (clienteCadastroId) {
      clienteId = clienteCadastroId;
    } else if (clienteNome) {
      let { data: cliente, error: clienteError } = await supabase
        .from('clientes_cadastro')
        .select('id, nome')
        .eq('nome', clienteNome)
        .maybeSingle();

      if (clienteError || !cliente) {
        const { data: clienteCI, error: clienteCIError } = await supabase
          .from('clientes_cadastro')
          .select('id, nome')
          .ilike('nome', clienteNome)
          .maybeSingle();

        if (!clienteCIError && clienteCI) {
          cliente = clienteCI;
          clienteError = null;
        }
      }

      if (clienteError || !cliente) {
        const { data: clientesSimilar, error: similarError } = await supabase
          .from('clientes_cadastro')
          .select('id, nome')
          .ilike('nome', `%${clienteNome}%`)
          .limit(5);

        if (!similarError && clientesSimilar && clientesSimilar.length > 0) {
          cliente = clientesSimilar[0];
          clienteError = null;
        }
      }

      if (clienteError || !cliente) {
        setClienteCache(cacheKey, 0);
        return 0;
      }

      clienteId = cliente.id;
    } else {
      setClienteCache(cacheKey, 0);
      return 0;
    }

    const valoresQuery = supabase
      .from('clientes_valores_historico')
      .select('valor_hora, data_reajuste, id')
      .eq('cliente_id', clienteId)
      .order('data_reajuste', { ascending: false })
      .limit(1);

    const { data: valoresHistorico, error: valorError } = options?.usarValorMaisRecente
      ? await valoresQuery
      : await valoresQuery.lte('data_reajuste', dataOS);

    if (valorError) {
      setClienteCache(cacheKey, 0);
      return 0;
    }

    const valorHistorico = valoresHistorico && valoresHistorico.length > 0 ? valoresHistorico[0] : null;
    const valor = valorHistorico?.valor_hora != null ? Number(valorHistorico.valor_hora) : 0;

    setClienteCache(cacheKey, valor);
    return valor;
  } catch {
    setClienteCache(cacheKey, 0);
    return 0;
  }
};

export const getFuncionarioComValorHora = async (operadorNome: string, dataAbertura: string): Promise<{ funcionario: Funcionario, valor_hora: number }> => {
  const defaultValorHora = 80;

  try {
    if (!operadorNome || !dataAbertura) throw new Error('Dados insuficientes');

    const { data: funcionario, error: funcionarioError } = await supabase
      .from('funcionarios')
      .select('*')
      .eq('nome', operadorNome)
      .maybeSingle();

    if (funcionarioError || !funcionario) throw new Error('Funcionário não encontrado');

    const { data: valoresHistorico, error: valorError } = await supabase
      .from('funcionarios_valores_historico')
      .select('valor_hora, data_reajuste, id')
      .eq('funcionario_id', funcionario.id)
      .lte('data_reajuste', dataAbertura)
      .order('data_reajuste', { ascending: false })
      .limit(1);

    if (valorError) return { funcionario, valor_hora: defaultValorHora };

    const valorHistorico = valoresHistorico?.[0];
    const valor_hora = valorHistorico?.valor_hora ? Number(valorHistorico.valor_hora) : defaultValorHora;

    return { funcionario, valor_hora };
  } catch {
    const mockFuncionario: Funcionario = {
      id: 'mock', nome: operadorNome, regime: 'CLT', ativo: false, created_at: '', updated_at: ''
    };
    return { funcionario: mockFuncionario, valor_hora: defaultValorHora };
  }
};
